import { Skeleton } from '@mantine/core';
import { formatHumanName } from '@medplum/core';
import { ReactElement } from 'react';
import { CommunicationDirection } from '@bariendo/lib/constants';
import { getProviderRoutingHistory, isForwardedCommunication } from '@bariendo/lib/communication';
import { VoiceCallOutcome } from '@bariendo/lib/voice-call-outcome';
import { OrganizationFragment, ProfileFragment } from '@/services/graphql';
import { TimelineVoiceCommunication } from '../types';
import { ActorAvatar } from './ActorAvatar';

// The partner timeline aliases Practitioner.name to practitionerName.
type ExtendedProfileFragment = ProfileFragment & { practitionerName?: Array<{ given?: string[]; family?: string }> };
type ExtendedOrganizationFragment = OrganizationFragment & { organizationName?: string };
type Participant = ExtendedProfileFragment | ExtendedOrganizationFragment;

function nameOf(participant: Participant | undefined): string | undefined {
  if (!participant) {
    return undefined;
  }
  if (participant.resourceType === 'Organization') {
    const org = participant as ExtendedOrganizationFragment;
    return org.organizationName ?? org.name ?? undefined;
  }
  const profile = participant as ExtendedProfileFragment;
  const name = profile.practitionerName?.[0] ?? profile.name?.[0];
  return name ? formatHumanName(name) || undefined : undefined;
}

/** The teammate on the call: who answered it inbound, who placed it outbound. */
export function getCallParticipantName(item: TimelineVoiceCommunication): string | undefined {
  const isIncoming = item.direction === CommunicationDirection.INCOMING;
  return nameOf((isIncoming ? item.recipient : item.sender) as Participant | undefined);
}

/** Who a missed call rang, off the routing extension's `display` — resolving
 * the reference would fetch. */
function routedToName(item: TimelineVoiceCommunication): string | undefined {
  const history = getProviderRoutingHistory(item.resource as Parameters<typeof getProviderRoutingHistory>[0]);
  return history.find((reference) => reference?.display)?.display;
}

export interface CallAttribution {
  /** Tooltip and accessible name. Says who, or says why we cannot. */
  label: string;
  /** Absent when nobody is recoverable — the avatar falls back to a glyph. */
  name?: string;
}

/**
 * Who a call is attributed to, in every state it can be in.
 *
 * Always names someone if anyone is recoverable. An unanswered call is where
 * attribution matters most — it is the one needing follow-up — so a missed
 * call names the provider it rang and a forwarded call names where it went.
 */
export function resolveCallAttribution(item: TimelineVoiceCommunication): CallAttribution {
  const isIncoming = item.direction === CommunicationDirection.INCOMING;
  const answered = !item.callOutcome || item.callOutcome === VoiceCallOutcome.ANSWERED;
  const participant = getCallParticipantName(item);

  if (!isIncoming) {
    return participant
      ? { name: participant, label: answered ? `Called by ${participant}` : `Placed by ${participant}` }
      : { label: answered ? 'Placed from Orbit' : 'Placed from Orbit — who called was not recorded' };
  }

  // An answered call is never described as unanswered, even when we cannot say
  // who took it.
  if (answered) {
    return participant
      ? { name: participant, label: `Answered by ${participant}` }
      : { label: 'Answered — who took it was not recorded' };
  }

  if (isForwardedCommunication(item.resource as Parameters<typeof isForwardedCommunication>[0])) {
    return participant
      ? { name: participant, label: `Forwarded to ${participant}` }
      : { label: 'Forwarded to the answering service' };
  }

  const routedTo = routedToName(item) ?? participant;
  return routedTo
    ? { name: routedTo, label: `Rang ${routedTo} — not answered` }
    : { label: 'Not answered — nobody was rung' };
}

/**
 * The header avatar on a call card — the teammate end of the line, replacing
 * the "Answered by <name>" pill. Costs no read: sender, recipient and the
 * routing extension's `display` are all in the timeline payload.
 */
export function CallParticipantAvatar({ item }: { item: TimelineVoiceCommunication }): ReactElement {
  const isIncoming = item.direction === CommunicationDirection.INCOMING;
  const attribution = resolveCallAttribution(item);
  const isForwarded = isForwardedCommunication(item.resource as Parameters<typeof isForwardedCommunication>[0]);

  // A ringing inbound call has no answerer yet — hold the slot rather than
  // letting the header reflow when one arrives. A forwarded call already went
  // somewhere, so it is not still ringing.
  if (isIncoming && item.status === 'preparation' && !isForwarded && !attribution.name) {
    return <Skeleton height={24} width={24} circle />;
  }

  return <ActorAvatar variant="teammate" label={attribution.label} name={attribution.name} />;
}
